import { GlobalApplicationState } from 'globalApplicationState';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import { ConnectedProps, connect, useDispatch, useSelector } from 'react-redux';
import { actions as insightActions } from 'modules/insights';
import { actions as audienceActions } from "modules/audiences";
import { GetTopicFilters, TopicGroup, defaultTopicFilter } from '..';
import TabContent from 'pages/common/tabContent';
import { Button, Checkbox, Divider, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from '@mui/material';
import TopicTableColumnHeader from './topicTableColumnHeader';
import Loading from 'modules/common/components/loading';
import TopicGroupRow from './topicGroupRow';
import { SNACKBAR_TIME_MS, TOPIC_COLUMNS_NUM, TOTAL_NUM_OF_COLUMNS } from '../styles/constants';
import ManagementEmptyResults from 'modules/common/components/managementEmptyResults';
import TopicFilters from './topicFilters';
import SuccessSnackbar from 'modules/common/components/snackbars/successSnackbar';
import TopicToolbar from './topicToolbar';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { getTopicGroups, topicSlice } from '../reducer';

const TopicListing: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [topicGroupsExpanded, setTopicGroupsExpanded] = React.useState<string[]>([]);
    const [topicsChecked, setTopicsChecked] = React.useState<string[]>([]);
    const [emptyGroupsChecked, setEmptyGroupsChecked] = React.useState<TopicGroup[]>([]);
    const [topicGroups, setTopicGroups] = React.useState<TopicGroup[]>([]);

    const [filters, setFilters] = React.useState<GetTopicFilters>(defaultTopicFilter);
    const [isSuccessSnackOn, setIsSuccessSnackOn] = React.useState<boolean>(false);
    const [toggleAll, setToggleAll] = React.useState<boolean>(false);

    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

    const {
        isFetching: isFetchingTopics,
        isUpdating: isSavingTopics,
        successMessage
    } = useSelector((state: GlobalApplicationState) => state.topics);

    const {
        tagStats,
        fetchTopChannels,
        audiences,
        getAudiences
    } = props;

    React.useEffect(() => {
        if (!audiences || audiences.length === 0) getAudiences();
    }, [audiences, getAudiences]);

    React.useEffect(() => {
        if (!tagStats) fetchTopChannels();
    }, [tagStats, fetchTopChannels]);

    React.useEffect(() => {
        if (successMessage.length !== 0) setIsSuccessSnackOn(true);
    }, [successMessage]);

    const emptyChecklist = React.useCallback(() => {
        setTopicsChecked([]);
        setEmptyGroupsChecked([]);
        setToggleAll(false);
    }, []);

    const isAllExpanded = (): boolean =>
        (topicGroupsExpanded.length === topicGroups.length)
        && (topicGroupsExpanded.length !== 0);

    const handleTopicGroupUpdate = React.useCallback(() => {
        const updateGroups = async (currentFilter?: GetTopicFilters) => {
            try {
                const groups = await dispatch(getTopicGroups(currentFilter)).unwrap();
                setTopicGroups(groups);
                setTopicGroupsExpanded(groups.map(tg => tg.id));
            }
            catch (e) {
                setTopicGroups([]);
            }
        };

        emptyChecklist();
        const currentFilter = filters.isFilterEnabled || !filters.ascending
            ? { ...filters, ascending: filters.ascending }
            : undefined;

        updateGroups(currentFilter);
    }, [filters, emptyChecklist, dispatch]);

    React.useEffect(() => {
        if (!isSavingTopics) handleTopicGroupUpdate();
    }, [isSavingTopics, handleTopicGroupUpdate]);

    const handleExpand = (groups?: TopicGroup[]) => {
        if (isAllExpanded()) {
            setTopicGroupsExpanded([]);
        }
        else {
            const tgs = groups ?? topicGroups;
            setTopicGroupsExpanded(tgs.map(tg => tg.id));
        }
    };

    const clearSuccessMessage = () => {
        setIsSuccessSnackOn(false);
        dispatch(topicSlice.actions.SET_SUCCESS_MSG(""));
    }

    const getSuccessSnackbar = (): JSX.Element => {
        return (
            <SuccessSnackbar
                successMessage={isSuccessSnackOn ? successMessage : ""}
                clearSuccessMessage={clearSuccessMessage}
                autoHideDuration={SNACKBAR_TIME_MS}
            />
        );
    };

    const getToolbar = (): JSX.Element =>
        <TopicToolbar
            selected={topicsChecked}
            topicGroups={topicGroups}
            emptyGroupsChecked={emptyGroupsChecked}
            uncheckAll={emptyChecklist}
        />;

    const onChangeFilters = (topicFilters: Partial<GetTopicFilters>) => {
        const updatedFilters = { ...filters, ...topicFilters };
        setFilters(updatedFilters);
    };

    const getFilter = (): JSX.Element =>
        <TopicFilters
            filters={filters}
            onSearch={onChangeFilters}
        />;

    const isAllChecked = (): boolean => {
        const totalTopicCount = topicGroups.flatMap(tg => tg.tags).length;
        const allEmptyGroups = topicGroups.filter(tg => tg.tags.length === 0);
        return totalTopicCount === topicsChecked.length
            && totalTopicCount !== 0
            && allEmptyGroups.length === emptyGroupsChecked.length;
    };

    const toggleCheckAll = () => {
        if (isAllChecked()) return emptyChecklist();

        const allTopics = topicGroups.flatMap(tg => tg.tags).map(t => t.id);
        const allEmptyGroups = topicGroups.filter(tg => tg.tags.length === 0);

        setTopicsChecked(allTopics);
        setEmptyGroupsChecked(allEmptyGroups);
        setToggleAll(true);
    };

    const toggleDirection = () => {
        const updatedFilters = { ...filters, ascending: !filters.ascending };
        setFilters(updatedFilters);
    };

    const getTableHeader = (): JSX.Element => <React.Fragment>
        <TableRow>
            <TableCell colSpan={TOPIC_COLUMNS_NUM} sx={{ backgroundColor: "background.paper"}}>
                {/* This empty column is here to act as sticky padding */}
            </TableCell>
        </TableRow>
        <TableRow>
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    indeterminate={topicsChecked.length > 0 && !isAllChecked()}
                    checked={isAllChecked()}
                    onClick={toggleCheckAll}
                    inputProps={{ "aria-label": "select all topics" }}
                />
            </TableCell>

            <TopicTableColumnHeader
                direction={filters.ascending ? "desc" : "asc"}
                toggleDirection={toggleDirection}
            />
        </TableRow>
    </React.Fragment>

    const onExpand = (expanded: boolean, groupId: string) => {
        if (expanded) {
            setTopicGroupsExpanded([...topicGroupsExpanded, groupId]);
        }
        else {
            setTopicGroupsExpanded(topicGroupsExpanded.filter((gId) => gId !== groupId));
        }
    };

    const onCheckRow = (checked: boolean, topicId: string) => {
        if (checked) {
            setTopicsChecked([...topicsChecked, topicId]);
        }
        else {
            setTopicsChecked(topicsChecked.filter((id) => id !== topicId));
        }
    };

    const onCheckGroup = (checked: boolean, topics: string[]) => {
        if (checked) {
            setTopicsChecked([...topicsChecked, ...topics]);
        }
        else {
            setTopicsChecked(topicsChecked.filter((id) => !topics.includes(id)));
        }
    };

    const onCheckEmptyGroup = (checked: boolean, group: TopicGroup) => {
        if (checked) {
            setEmptyGroupsChecked([...emptyGroupsChecked, group]);
        }
        else {
            setEmptyGroupsChecked(emptyGroupsChecked.filter((tg) => tg.id !== group.id));
        }
    };

    const getTableBody = (): JSX.Element => {
        const isExpanded = (groupId: string) => {
            return topicGroupsExpanded.findIndex((gId: string) => gId === groupId) !== -1;
        };

        return (
            <React.Fragment>
                {topicGroups.map((tg) => (
                    <TopicGroupRow
                        key={tg.id}
                        topicGroup={tg}
                        topicsChecked={topicsChecked}
                        toggleCheckAll={toggleAll}
                        onCheckRow={onCheckRow}
                        onCheckGroup={onCheckGroup}
                        onCheckEmptyGroup={onCheckEmptyGroup}
                        expanded={isExpanded(tg.id)}
                        onToggleExpand={onExpand}
                    />
                ))}
            </React.Fragment>
        );
    };

    const getTableFooter = (): JSX.Element => {
        const collapseMsg = "Collapse All";
        const expandMsg = "Expand All";

        return (
            <React.Fragment>
                <TableRow>
                    <TableCell colSpan={TOTAL_NUM_OF_COLUMNS}>
                        <Divider />
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell
                        colSpan={TOTAL_NUM_OF_COLUMNS}
                        align='right'
                        sx={{ paddingTop: "5px" }}
                    >
                        <Button onClick={_ => handleExpand()}>
                            {isAllExpanded() ? collapseMsg : expandMsg}
                        </Button>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    };

    const getList = (): JSX.Element => {
        return (
            <Table stickyHeader size="medium">
                <TableHead className="sticky-multirow-header">
                    {getTableHeader()}
                </TableHead>

                <TableBody>
                    {getTableBody()}
                </TableBody>

                {
                    topicGroups.length !== 0 &&
                    <TableFooter className="sticky-footer" sx={{ backgroundColor: "background.paper"}}>
                        {getTableFooter()}
                    </TableFooter>
                }
            </Table>
        );
    };

    const getTableContents = (): JSX.Element => {
        if (!props.tagStats || isFetchingTopics || isSavingTopics) {
            return <Loading />;
        }
        else if (topicGroups.length === 0) {
            return (
                <React.Fragment>
                    {getList()}
                    {<ManagementEmptyResults searchType={"Topics"} hasFilters={false} />}
                </React.Fragment>
            );
        }
        else {
            return (
                <React.Fragment>
                    {
                        filters.isFilterEnabled &&
                        <Typography variant="h2" className="topic-management-result">Results</Typography>
                    }
                    {getList()}
                </React.Fragment>
            );
        }
    };

    return (
        <TabContent>
            {getToolbar()}
            {getFilter()}
            {getTableContents()}
            {getSuccessSnackbar()}
        </TabContent>
    );
};

interface ComponentProps {
}

const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        tagStats: state.insights.topChannels,
        audiences: state.audiences.audiences
    }),
    {
        fetchTopChannels: insightActions.fetchTopChannels,
        getAudiences: audienceActions.getAudiences,
    }
);

type PropsWithRedux = ConnectedProps<typeof connector>;

export default injectIntl(connector(TopicListing));